import { useCallback, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { ValueType } from 'components/DebounceSelect';
import { GeoSearchResult, getGeoSearch } from 'lib/api/getGeoSearch';
import { Maybe } from 'types/UtilityTypes';
import { GenericCompanySearchableFields } from 'utils/loadInfoAndBuilding';

import { QuickQuoteInputs } from '../../types';
import { QuoteCountries } from '../../types';
import { parseLocation } from '../../utils/location';

export function mapGeoResultsToOptions(
  results: Maybe<GeoSearchResult[]>
): ValueType[] {
  if (!results) {
    return [];
  }

  return results.map((r) => ({ label: r.value, value: r.id }));
}

/**
 * Manages geo search state for a single location field.
 * Fetches results from the geo search endpoint on each keystroke and retains
 * the selected result in the list so RHFDebounceSelect can resolve the display label.
 */
export function useGeoSearch(
  setValue: UseFormSetValue<QuickQuoteInputs>,
  stopIndex: number
) {
  const [results, setResults] = useState<Maybe<GeoSearchResult[]>>(null);
  const selectedRef = useRef<Maybe<GeoSearchResult>>(null);
  const fetchRef = useRef(0);

  const fetchOptions = async (
    _field: GenericCompanySearchableFields,
    query: string
  ): Promise<ValueType[]> => {
    fetchRef.current += 1;
    const fetchId = fetchRef.current;

    const res = await getGeoSearch(query);
    if (res.isErr()) {
      return [];
    }

    if (fetchId !== fetchRef.current) {
      return [];
    }

    setResults(res.value.results);
    return mapGeoResultsToOptions(res.value.results);
  };

  const handleSelect = (value: ValueType, selectedRawItem?: unknown) => {
    if (!value) {
      selectedRef.current = null;
      setValue(`stops.${stopIndex}.city`, '');
      setValue(`stops.${stopIndex}.state`, '');
      setValue(`stops.${stopIndex}.zip`, '');
      setValue(`stops.${stopIndex}.country`, undefined);
      setValue(`stops.${stopIndex}.locationDisplay`, '');
      setValue(`stops.${stopIndex}.externalTMSID`, '');
      setValue(`stops.${stopIndex}.timezone`, '');
      setValue(`stops.${stopIndex}.addressName`, '');
      setValue(`stops.${stopIndex}.addressLine1`, '');
      setValue(`stops.${stopIndex}.addressLine2`, '');
      return;
    }

    const id =
      typeof value === 'object' && 'value' in value ? value.value : value;

    const match =
      (selectedRawItem as Maybe<GeoSearchResult>) ??
      results?.find((r) => r.id === String(id));

    if (match) {
      selectedRef.current = match;
      // Keep the selected result in the list so mapOptions can find it for display
      setResults((prev) => {
        const base = prev ?? [];
        return base.some((r) => r.id === match.id) ? base : [match, ...base];
      });

      // Prefer structured fields from BE if available; fallback to parsing display value.
      const matchObj = match as unknown as Record<string, unknown>;
      const address =
        (matchObj.address as Maybe<Record<string, unknown>>) ?? undefined;
      const getStr = (value: unknown) =>
        typeof value === 'string' ? value.trim() : '';

      // Keep the selected option value as geo ID so RHF select can stay controlled.
      setValue(`stops.${stopIndex}.location`, String(match.id));
      setValue(`stops.${stopIndex}.locationDisplay`, match.value);
      const parsed = parseLocation(match.value);

      const city =
        getStr(address?.city) || getStr(matchObj.city) || parsed?.city || '';
      const state =
        getStr(address?.state) || getStr(matchObj.state) || parsed?.state || '';
      const zip =
        getStr(address?.zip) || getStr(matchObj.zip) || parsed?.zip || '';
      const timezone = getStr(address?.timezone) || getStr(matchObj.timezone);
      const addressName = getStr(address?.name);
      const addressLine1 = getStr(address?.addressLine1);
      const addressLine2 = getStr(address?.addressLine2);

      const rawCountry =
        getStr(address?.country).toUpperCase() ||
        getStr(matchObj.country).toUpperCase();
      const country =
        rawCountry === QuoteCountries.Canada
          ? QuoteCountries.Canada
          : rawCountry === QuoteCountries.USA
            ? QuoteCountries.USA
            : parsed?.country;

      // Always set fields to avoid stale values from previous selection.
      setValue(`stops.${stopIndex}.city`, city);
      setValue(`stops.${stopIndex}.state`, state);
      setValue(`stops.${stopIndex}.zip`, zip);
      setValue(`stops.${stopIndex}.country`, country);
      setValue(`stops.${stopIndex}.externalTMSID`, String(match.id));
      setValue(`stops.${stopIndex}.timezone`, timezone);
      setValue(`stops.${stopIndex}.addressName`, addressName);
      setValue(`stops.${stopIndex}.addressLine1`, addressLine1);
      setValue(`stops.${stopIndex}.addressLine2`, addressLine2);
    }
  };

  /** Run geo search with the given query (e.g. when dropdown opens with existing text from AI). */
  const fetchWithQuery = useCallback(async (query: string) => {
    const q = query?.trim();
    if (!q) {
      return;
    }

    const res = await getGeoSearch(q);
    if (res.isOk()) {
      setResults(res.value.results);
    }
  }, []);

  return { results, fetchOptions, handleSelect, fetchWithQuery };
}
